/**
 * payment-service calls of Settings → Payments (T11-39) and the card-terminal statement check /
 * refunds list (T11-40), design/04 §13, design/06 §3:
 *
 * | what | endpoint | permission |
 * |---|---|---|
 * | ways to take money (+ what can be added) | `GET /v1/admin/payment-methods` | payments.read |
 * | add a way / a card terminal | `POST /v1/admin/payment-methods` | payments.settings |
 * | change one (If-Match version) · switch off | `PATCH` / `DELETE /v1/admin/payment-methods/{id}` | payments.settings |
 * | "what would the fee be" (unsaved values too) | `POST /v1/admin/payments/fee-preview` | payments.read |
 * | what the terminal should have settled | `GET /v1/admin/reconciliation/edc/expected?terminalId=&from=&to=&groupBy=` | payments.reconcile |
 * | upload a statement · list · one with its lines | `POST/GET /v1/admin/reconciliation/edc/statements[/{id}]` | payments.reconcile |
 * | match / ignore / record a forgotten sale | `POST …/statements/{id}/lines/{lineId}/resolve` | payments.reconcile (+ payments.confirm) |
 * | the checked statement as CSV | `GET …/statements/{id}/export.csv` | payments.reconcile |
 * | card payments of a terminal (to match by hand) | `GET /v1/admin/payments?terminalId=&from=&to=` | payments.read |
 * | every refund | `GET /v1/admin/refunds?status=` | payments.read |
 *
 * Fees, totals and differences are payment-service's; the admin never multiplies a percentage.
 */
import {
  createPaymentsClient,
  type PaymentsPaths,
  type ServiceClientOptions,
} from "@tcg/api-client";
import { useMemo } from "react";

import { apiErrorFrom, unwrap } from "@/lib/api/errors";
import { useClientOptions } from "@/lib/api/use-api";

type Json<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Body<T> = T extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : T extends { requestBody?: { content: { "application/json": infer B } } }
    ? B
    : never;
type P = PaymentsPaths;

export type MethodList = Json<P["/v1/admin/payment-methods"]["get"]["responses"][200]>;
export type PaymentMethodRow = MethodList["data"][number];
export type ProviderRow = NonNullable<MethodList["providers"]>[number];
export type MethodCreateIn = Body<P["/v1/admin/payment-methods"]["post"]>;
export type MethodPatchIn = Body<P["/v1/admin/payment-methods/{method_id}"]["patch"]>;
export type FeePreviewIn = Body<P["/v1/admin/payments/fee-preview"]["post"]>;
export type FeePreview = Json<P["/v1/admin/payments/fee-preview"]["post"]["responses"][200]>;
export type InstructionsIn = NonNullable<MethodCreateIn["instructions"]>;
export type BankAccountIn = NonNullable<InstructionsIn["bankAccount"]>;
export type FeeBearer = NonNullable<MethodCreateIn["feeBearer"]>;

export type EdcExpected = Json<P["/v1/admin/reconciliation/edc/expected"]["get"]["responses"][200]>;
export type EdcStatementIn = Body<P["/v1/admin/reconciliation/edc/statements"]["post"]>;
export type EdcBatch = Json<
  P["/v1/admin/reconciliation/edc/statements/{batch_id}"]["get"]["responses"][200]
>;
export type EdcBatchRow = Json<
  P["/v1/admin/reconciliation/edc/statements"]["get"]["responses"][200]
>["data"][number];
export type EdcLine = NonNullable<EdcBatch["lines"]>[number];
export type EdcLineStatus = EdcLine["status"];
export type EdcResolveIn = Body<
  P["/v1/admin/reconciliation/edc/statements/{batch_id}/lines/{line_id}/resolve"]["post"]
>;
export type AdminPaymentRow = Json<
  P["/v1/admin/payments"]["get"]["responses"][200]
>["data"][number];
export type RefundListPage = Json<P["/v1/admin/refunds"]["get"]["responses"][200]>;
export type AdminRefund = RefundListPage["data"][number];
export type AdminRefundStatus = AdminRefund["status"];

export interface PaymentSettingsApi {
  methods(signal?: AbortSignal): Promise<MethodList>;
  create(body: MethodCreateIn): Promise<PaymentMethodRow>;
  patch(id: string, body: MethodPatchIn, version: number): Promise<PaymentMethodRow>;
  remove(id: string, version: number): Promise<PaymentMethodRow>;
  feePreview(body: FeePreviewIn, signal?: AbortSignal): Promise<FeePreview>;

  expected(
    query: { terminalId: string; from: string; to: string; groupBy: "day" | "brand" },
    signal?: AbortSignal,
  ): Promise<EdcExpected>;
  upload(body: EdcStatementIn, key: string): Promise<EdcBatch>;
  statements(terminalId: string | null, signal?: AbortSignal): Promise<EdcBatchRow[]>;
  statement(id: string, signal?: AbortSignal): Promise<EdcBatch>;
  resolve(batchId: string, lineId: string, body: EdcResolveIn, key: string): Promise<EdcLine>;
  statementCsv(id: string): Promise<Blob>;
  terminalPayments(
    query: { terminalId: string; from: string; to: string },
    signal?: AbortSignal,
  ): Promise<AdminPaymentRow[]>;

  refunds(
    query: { status: AdminRefundStatus[] | null; limit: number; cursor: string | null },
    signal?: AbortSignal,
  ): Promise<RefundListPage>;
}

const ifMatch = (version: number) => ({ "If-Match": `"${String(version)}"` });

export function createPaymentSettingsApi(options: ServiceClientOptions): PaymentSettingsApi {
  const client = createPaymentsClient(options);
  const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
  return {
    methods: (signal) => unwrap(client.GET("/v1/admin/payment-methods", { ...withSignal(signal) })),
    create: (body) => unwrap(client.POST("/v1/admin/payment-methods", { body })),
    patch: (id, body, version) =>
      unwrap(
        client.PATCH("/v1/admin/payment-methods/{method_id}", {
          params: { path: { method_id: id }, header: ifMatch(version) },
          body,
        }),
      ),
    remove: (id, version) =>
      unwrap(
        client.DELETE("/v1/admin/payment-methods/{method_id}", {
          params: { path: { method_id: id }, header: ifMatch(version) },
        }),
      ),
    feePreview: (body, signal) =>
      unwrap(client.POST("/v1/admin/payments/fee-preview", { body, ...withSignal(signal) })),

    expected: (query, signal) =>
      unwrap(
        client.GET("/v1/admin/reconciliation/edc/expected", {
          params: { query },
          ...withSignal(signal),
        }),
      ),
    upload: (body, key) =>
      unwrap(
        client.POST("/v1/admin/reconciliation/edc/statements", {
          params: { header: { "Idempotency-Key": key } },
          body,
        }),
      ),
    statements: async (terminalId, signal) =>
      (
        await unwrap(
          client.GET("/v1/admin/reconciliation/edc/statements", {
            params: { query: { limit: 50, ...(terminalId ? { terminalId } : {}) } },
            ...withSignal(signal),
          }),
        )
      ).data,
    statement: (id, signal) =>
      unwrap(
        client.GET("/v1/admin/reconciliation/edc/statements/{batch_id}", {
          params: { path: { batch_id: id } },
          ...withSignal(signal),
        }),
      ),
    resolve: (batchId, lineId, body, key) =>
      unwrap(
        client.POST("/v1/admin/reconciliation/edc/statements/{batch_id}/lines/{line_id}/resolve", {
          params: {
            path: { batch_id: batchId, line_id: lineId },
            header: { "Idempotency-Key": key },
          },
          body,
        }),
      ),
    statementCsv: async (id) => {
      const result = await client.GET(
        "/v1/admin/reconciliation/edc/statements/{batch_id}/export.csv",
        { params: { path: { batch_id: id } }, parseAs: "blob" },
      );
      if (!result.response.ok || !result.data) {
        throw apiErrorFrom(result.response, result.error);
      }
      return result.data as Blob;
    },
    terminalPayments: async (query, signal) =>
      (
        await unwrap(
          client.GET("/v1/admin/payments", {
            params: {
              query: {
                terminalId: query.terminalId,
                from: query.from,
                to: query.to,
                sort: "created_desc",
                limit: 100,
              },
            },
            ...withSignal(signal),
          }),
        )
      ).data,

    refunds: (query, signal) =>
      unwrap(
        client.GET("/v1/admin/refunds", {
          params: {
            query: {
              limit: query.limit,
              ...(query.status?.length ? { status: query.status } : {}),
              ...(query.cursor ? { cursor: query.cursor } : {}),
            },
          },
          ...withSignal(signal),
        }),
      ),
  };
}

export function usePaymentSettingsApi(): PaymentSettingsApi {
  const options = useClientOptions();
  return useMemo(() => createPaymentSettingsApi(options), [options]);
}

export const PAYMENT_SETTINGS_KEY = ["payments", "settings"] as const;
export const RECONCILIATION_KEY = ["payments", "reconciliation"] as const;
export const ALL_REFUNDS_KEY = ["payments", "all-refunds"] as const;
